import { useState } from 'react';

import { proposalPath } from '@/app/scope';
import { useCan } from '@/auth/roles';
import { Button, LinkButton } from '@/components/Button';
import { DIALOG_LINGER_MS, useLingering } from '@/components/motion';
import type { Proposal } from '@/domain/types';

import {
  ApproveDialog,
  RedecideDialog,
  RejectDialog,
  RetryDialog,
  type DecisionOutcome,
} from './dialogs';

type Open = 'approve' | 'reject' | 'redecide' | 'retry' | null;

/**
 * The buttons a proposal offers in its current state (TDD §5.4), with their dialogs. Used by
 * the queue cards and the proposal page so the two behave identically. A 409 is reported
 * through `onOutcome` so the page can show the ConflictDialog: after a conflict the queue
 * refetches and the card may unmount, so the dialog must live above the card.
 */
export function ProposalActions({
  proposal,
  displayName,
  size = 'md',
  showDetailsLink = false,
  onOutcome,
}: {
  proposal: Proposal;
  displayName: string;
  size?: 'sm' | 'md';
  showDetailsLink?: boolean;
  onOutcome?: (outcome: DecisionOutcome) => void;
}) {
  const [open, setOpen] = useState<Open>(null);
  // The buttons that decide (CHG-050): approvers and admins; the API refuses anyone else.
  const canDecide = useCan('approver');
  // The dialog stays mounted, closed, for a moment after a decision so it can fade out.
  const shown = useLingering(open, DIALOG_LINGER_MS);

  const done = (outcome: DecisionOutcome) => {
    setOpen(null);
    onOutcome?.(outcome);
  };

  const common = { proposal, displayName, onClose: () => setOpen(null), onDone: done };

  return (
    <div className="flex flex-wrap items-center gap-2">
      {canDecide && proposal.status === 'open' ? (
        <>
          <Button variant="primary" size={size} onClick={() => setOpen('approve')}>
            Approve…
          </Button>
          <Button size={size} onClick={() => setOpen('reject')}>
            Reject…
          </Button>
        </>
      ) : null}
      {canDecide && proposal.status === 'publish_failed' ? (
        <>
          <Button variant="primary" size={size} onClick={() => setOpen('retry')}>
            Retry now…
          </Button>
          <Button size={size} onClick={() => setOpen('redecide')}>
            Decide again…
          </Button>
        </>
      ) : null}
      {showDetailsLink ? (
        <LinkButton to={proposalPath(proposal.proposal_id)} variant="ghost" size={size}>
          Details
        </LinkButton>
      ) : null}

      {shown === 'approve' ? <ApproveDialog {...common} open={open === 'approve'} /> : null}
      {shown === 'reject' ? <RejectDialog {...common} open={open === 'reject'} /> : null}
      {shown === 'redecide' ? <RedecideDialog {...common} open={open === 'redecide'} /> : null}
      {shown === 'retry' ? <RetryDialog {...common} open={open === 'retry'} /> : null}
    </div>
  );
}
